import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import NutMotif from "./NutMotif";

const TIMELINE = [
  {
    year: "2007",
    title: "Works established",
    body: "Rahil Engineering Works starts out at Makarpura GIDC, Vadodara, machining stud nuts and bolts for local process plants.",
  },
  {
    year: "2012",
    title: "Range widened",
    body: "Capacity added for threaded rods, heavy hex nuts and customised engineering solutions as EPC contractors begin placing project-wise bolting scopes.",
  },
  {
    year: "2018",
    title: "ISO 9001:2015",
    body: "The quality management system is certified, with batch traceability and mill test certificates issued as standard on every dispatch.",
  },
  {
    year: "Today",
    title: "Nine industries served",
    body: "From oil & gas and power to defence and railways — supplying high tensile and stainless fasteners from M12 to M100 in any length.",
  },
];

const LEADERSHIP = [
  { name: "Manish R Shah", role: "Founder & Works Head", phone: "8000151777", alt: "9375267221" },
  { name: "Rahil Shah", role: "Operations & Sales", phone: "8905853080" },
];

export default function About() {
  return (
    <section
      id="about"
      className="relative bg-white bg-blueprint py-20 lg:py-28 overflow-hidden"
      data-testid="about-section"
    >
      <NutMotif size={640} className="-right-56 top-10" opacity={0.055} reverse duration={100} />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.65 }}
          className="lg:col-span-5"
        >
          <div className="flex items-center gap-3 text-[11px] sm:text-xs tracking-[0.12em] uppercase text-steel font-semibold mb-4">
            <span className="w-10 h-[2px] bg-brand" />
            About the company
          </div>
          <h2 className="font-display font-extrabold uppercase tracking-tight leading-[1.02] text-4xl sm:text-5xl text-ink">
            A Vadodara
            <br />
            fastener works
            <br />
            since 2007
          </h2>
          <div className="mt-6 space-y-4 text-sm sm:text-base text-slate-600 leading-relaxed">
            <p>
              Rahil Engineering Works is a family-run manufacturer and supplier
              of high tensile and stainless steel fasteners, operating from
              303/22 Makarpura GIDC in Vadodara, Gujarat. What began as a small
              machining unit in 2007 now serves refineries, power plants,
              chemical complexes, rolling stock builders and defence suppliers.
            </p>
            <p>
              Our philosophy is simple: quality is never by chance, it is by
              choice. Every fastener is designed and developed to the
              specification of the client, produced in-house, and released only
              after material and dimensional verification.
            </p>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-3" data-testid="about-leadership">
            {LEADERSHIP.map((p) => (
              <div
                key={p.name}
                className="border border-steel-light rounded-xl bg-white px-5 py-4 hover:border-brand transition-colors duration-300"
                data-testid={`leader-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="font-display font-bold uppercase text-sm text-ink">{p.name}</div>
                <div className="font-sans font-semibold text-[11px] tracking-[0.12em] uppercase text-steel mt-1">
                  {p.role}
                </div>
                <a
                  href={`tel:+91${p.phone}`}
                  className="mt-3 inline-flex items-center gap-2 text-[13px] font-semibold text-brand hover:underline"
                >
                  <Phone size={13} />
                  {p.phone}
                  {p.alt ? ` / ${p.alt}` : ""}
                </a>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="lg:col-span-7">
          <div className="relative pl-8 sm:pl-10">
            <span className="absolute left-[7px] top-2 bottom-2 w-px bg-steel-light" />
            {TIMELINE.map((t, i) => (
              <motion.div
                key={t.year}
                initial={{ opacity: 0, x: 18 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.55, delay: i * 0.1 }}
                className="relative pb-9 last:pb-0 group"
                data-testid={`timeline-${i + 1}`}
              >
                <span className="absolute -left-8 sm:-left-10 top-1.5">
                  <svg width="16" height="16" viewBox="0 0 16 16" className="transition-transform duration-500 group-hover:scale-[1.18]">
                    <polygon points="8,0 15,4 15,12 8,16 1,12 1,4" fill="#1B5FAA" />
                    <circle cx="8" cy="8" r="2.6" fill="#ffffff" />
                  </svg>
                </span>
                <div className="font-sans font-semibold text-[11px] tracking-[0.12em] text-brand mb-1.5">{t.year}</div>
                <h3 className="font-display font-bold uppercase text-lg sm:text-xl text-ink leading-snug">
                  {t.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-xl">{t.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
